import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Avatar } from "@/components/Avatar";
import { toast } from "sonner";
import { Monitor, CheckCircle2, XCircle } from "lucide-react";

export default function QrApprove() {
  const { sid } = useParams();
  const { user, loading } = useAuth();
  const [state, setState] = useState("READY"); // READY | APPROVED | REJECTED | ERROR
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      localStorage.setItem("stream_qr_pending", sid);
      nav("/login", { replace: true });
    }
  }, [loading, user, sid, nav]);

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/auth/qr/session/${sid}/${action}`);
      setState(action === "approve" ? "APPROVED" : "REJECTED");
      if (action === "approve") toast.success("Prijava na računalu odobrena");
    } catch (e) { toast.error(formatError(e)); setState("ERROR"); }
    setBusy(false);
  };

  if (loading || !user) return null;

  return (
    <div className="stream-dark min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/dashboard" className="flex justify-center mb-8"><Logo size="xl" light /></Link>
        <div className="card-dark p-8 text-center" data-testid="qr-approve-card">
          {state === "READY" && (
            <>
              <div className="w-16 h-16 rounded-full bg-[#FFD60A]/10 border border-[#FFD60A]/30 flex items-center justify-center mx-auto mb-5">
                <Monitor size={28} className="text-[#FFD60A]" />
              </div>
              <h1 className="font-heading font-black text-2xl text-white mb-2">Prijava na računalu</h1>
              <p className="text-white/60 text-sm mb-6">Želite li prijaviti svoj račun na računalu koje je prikazalo ovaj QR kod?</p>
              <div className="flex items-center justify-center gap-3 mb-7 p-3 rounded-xl bg-white/5 border border-white/10">
                <Avatar user={user} size={40} />
                <div className="text-left min-w-0">
                  <div className="text-white text-sm font-semibold truncate">{user.first_name} {user.last_name}</div>
                  <div className="text-white/40 text-xs truncate">@{user.username}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button className="btn-yellow rounded-full flex-1 h-12" disabled={busy} onClick={() => act("approve")} data-testid="qr-approve-btn">Odobri prijavu</Button>
                <Button variant="ghost" className="text-red-400 hover:bg-red-500/10 rounded-full flex-1 h-12" disabled={busy} onClick={() => act("reject")} data-testid="qr-reject-btn">Odbij</Button>
              </div>
            </>
          )}
          {state === "APPROVED" && (
            <>
              <CheckCircle2 size={64} className="mx-auto text-green-400 mb-5" />
              <h1 className="font-heading font-black text-2xl text-white mb-2">Prijava odobrena</h1>
              <p className="text-white/60 text-sm">Vaše računalo je sada prijavljeno. Možete zatvoriti ovu stranicu.</p>
            </>
          )}
          {state === "REJECTED" && (
            <>
              <XCircle size={64} className="mx-auto text-red-400 mb-5" />
              <h1 className="font-heading font-black text-2xl text-white mb-2">Prijava odbijena</h1>
              <p className="text-white/60 text-sm">Nitko se nije prijavio na vaš račun.</p>
            </>
          )}
          {state === "ERROR" && <p className="text-white/70">QR kod je istekao. Osvježite stranicu na računalu i skenirajte novi kod.</p>}
          <Link to="/dashboard"><Button variant="ghost" className="text-white/70 mt-6" data-testid="qr-to-dashboard">Nazad na dashboard</Button></Link>
        </div>
      </div>
    </div>
  );
}
